import type { ZudokuConfig } from "zudoku";

const config: ZudokuConfig = {
  port: 3000,
  theme: {
    "light": {
      "background": "#FFFFFF",
      "foreground": "#111827",
      "muted": "#F3F4F6",
      "mutedForeground": "#6B7280",
      "card": "#FFFFFF",
      "cardForeground": "#111827",
      "border": "#E5E7EB",
      "input": "#E5E7EB",
      "primary": "#8B5CF6",
      "primaryForeground": "#FFFFFF",
      "secondary": "#9333EA",
      "secondaryForeground": "#FFFFFF",
      "accent": "#A855F7",
      "accentForeground": "#FFFFFF",
      "destructive": "#DC2626",
      "destructiveForeground": "#FFFFFF",
      "ring": "#8B5CF6"
    },
    dark: {
      "background": "#0B0B0E",
      "foreground": "#FFFFFF",
      "muted": "#1A1A1E",
      "mutedForeground": "#9CA3AF",
      "popover": "#111113",
      "popoverForeground": "#E5E7EB",
      "card": "#121214",
      "cardForeground": "#E5E7EB",
      "border": "#2D2D33",
      "input": "#2D2D33",
      "primary": "#9B5CF6",
      "primaryForeground": "#FFFFFF",
      "secondary": "#7E22CE",
      "secondaryForeground": "#FFFFFF",
      "accent": "#A855F7",
      "accentForeground": "#FFFFFF",
      "destructive": "#EF4444",
      "destructiveForeground": "#FFFFFF",
      "ring": "#A855F7",
      "radius": "0.5rem"
    },
  },
  docs: {
    defaultOptions: {
      toc: true, // Show TOC by default
    }
  },
  site: {
    showPoweredBy: true,
    title: "NexQR Docs",
    logo: {
      src: { light: "/ic_logo_light.png", dark: "/ic_logo_dark.png" },
      alt: "Zudoku",
      width: "130px",
    },
    // banner: {
    //   message: "Welcome to our beta documentation!",
    //   color: "info", // "note" | "tip" | "info" | "caution" | "danger" or custom
    //   dismissible: true
    // },
    footer: {
      position: "center",
      copyright: `© ${new Date().getFullYear()} NexQR. All rights reserved.`,
      columns: [
        {
          title: "Product",
          position: "center", // position in grid, optional: start, center, end
          links: [
            { label: "Features", href: "/features" },
            { label: "Pricing", href: "/pricing" },
            { label: "Documentation", href: "/docs" },
            { label: "GitHub", href: "https://github.com/org/repo" }, // Auto-detected as external
          ],
        },
        {
          title: "Company",
          links: [
            { label: "About", href: "/about" },
            { label: "Blog", href: "/blog" },
            { label: "Contact", href: "/contact" },
          ],
        },
      ],
      social: [
        {
          icon: "github",
          href: "https://github.com/yourusername",
        },
        {
          icon: "telegram",
          href: "https://twitter.com/yourhandle",
        },
        {
          icon: "facebook",
          href: "https://twitter.com/yourhandle",
        },
      ],
      logo: {
        src: {
          light: "/ic_logo_light.png",
          dark: "/ic_logo_dark.png",
        },
        alt: "Company Logo",
        width: "120px" // optional width
      },
    },
  },
  search: {
    type: "pagefind",
    // Optional: Maximum number of sub results per page
    maxSubResults: 3,
    // Optional: Configure search result ranking (defaults shown below)
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
  slots: {
    "footer-before": () => (
      <div>
        <h3>Custom pre-footer content</h3>
        <p>This appears before the columns</p>
      </div>
    ),
    "footer-after": () => (
      <div>
        <p>Additional footer content</p>
      </div>
    )
  },
  metadata: {
    title: "Example Website Title",
    description: "This is an example description for the website.",
    logo: "https://example.com/logo.png",
    favicon: "https://example.com/favicon.ico",
    generator: "Website Generator 1.0",
    applicationName: "Example App",
    referrer: "no-referrer",
    keywords: ["example", "website", "metadata", "SEO"],
    authors: ["John Doe", "Jane Smith"],
    creator: "John Doe",
    publisher: "Example Publisher Inc."
  },
  navigation: [
    {
      type: "doc",
      file: "landing",
      path: "/",
      display: "hide",
    },
    {
      type: "category",
      label: "Guides",
      icon: "book",
      items: [
        {
          type: "doc",
          label: "General",
          icon: "cog",
          file: "/guides/general",
        },
        {
          type: "category",
          label: "Products",
          icon: "package",
          collapsed: false,
          items: [
            {
              type: "category",
              label: "Categories",
              icon: "blocks",
              collapsed: true,
              items: [
                {
                  type: "doc",
                  label: "Add Category",
                  file: "/guides/category/add_category",
                },
                {
                  type: "doc",
                  label: "Category List",
                  file: "/guides/category/category_list",
                }
              ],
            },
            {
              type: "category",
              label: "Options Set",
              icon: "receipt-text",
              collapsed: true,
              items: [
                {
                  type: "doc",
                  label: "Add Option",
                  file: "/guides/options/add_options",
                },
                {
                  type: "doc",
                  label: "Options List",
                  file: "/guides/options/options_list",
                }
              ],
            },
            {
              type: "category",
              label: "Add On",
              icon: "toy-brick",
              collapsed: true,
              items: [
                {
                  type: "doc",
                  label: "Add Add On",
                  file: "/guides/add_on/add_add_on",
                },
                {
                  type: "doc",
                  label: "Add On List",
                  file: "/guides/add_on/add_on_list",
                }
              ],
            },
            {
              type: "doc",
              label: "Add Product",
              file: "/guides/products/add_product",
            },
            {
              type: "doc",
              label: "Product List",
              file: "/guides/products/product_list",
            },
          ],
        },
        {
          type: "category",
          label: "Orders",
          icon: "list",
          collapsed: false,
          items: [
            {
              type: "doc",
              label: "Order List",
              file: "/guides/order/order_list",
            },
          ],
        },
      ],
    },
    {
      type: "link",
      to: "/api",
      label: "API Reference",
    },
  ],
  apis: [
    {
      type: "file",
      input: "./apis/openapi.yaml",
      path: "/api",
    },
    // {
    //   type: "file",
    //   input: "./apis/openapi.yaml",
    //   path: "/api",
    // },
  ],
};

export default config;
